import { debounce } from 'lodash-es'
import type { Timeout } from '../../types'
import { ScreenStateX } from '../../types/mfc'
import { setFullWindow } from '../actions/layout'
import { pinRoom, showPinRoomChat } from '../actions/room'
import {
    closeFloatingPreview,
    getModalState,
    repositionFloater,
} from '../app-state/modals'
import APP_BREAKPOINTS from '../components/styles/breakpoints'
import {
    COMPONENT_HEIGHTS,
    COMPONENT_WIDTHS,
    getBrowserBottomInset,
    PINNED_ROOM_CHAT_MIN_WIDTH,
    PINNED_ROOM_MAX_HEIGHT,
    PINNED_ROOM_MIN_WIDTH_FOR_CHAT,
} from '../components/theme'
import { FloaterPageState, LayoutEvent, SettingsType } from '../constants/enums'
import { FLOATING_PREVIEW } from '../constants/floaters'
import { pubSubLayout } from '../state/pubSub'
import { APPSTATE } from '../state/state.app'
import { LAYOUTSTATE } from '../state/state.layout'
import { SETTINGSSTATE } from '../state/state.settings'
import { SIDEBAR, SidebarType } from '../state/state.sidebar'
import {
    isIDevice,
    isScreenOrientationSupported,
    isTouchDevice,
} from '../utilities/browser'
import { getHeight, getWidth } from '../utilities/dimensions'
import { pubSub } from './pubSub'
import { customStateWrapper, ROOMSSTATE, SCREEN_STATE } from './state'

export type Size = {
    width: number
    height: number
}

const setScreenState = customStateWrapper<ScreenStateX>(() => {
    pubSub.SCREEN.emit('state', SCREEN_STATE.screen, true)
})((value) => {
    SCREEN_STATE.screen = { ...SCREEN_STATE.screen, ...value }
})

export const getScreenState = () => SCREEN_STATE.screen

export const resolveFullWindow = () => {
    const { portrait: isVertical } = getScreenState()
    const inRoomOrMessage =
        document.location.pathname.includes('/room/') ||
        document.location.pathname.includes('/message/')

    const { fullWindow: fullWindowState } = LAYOUTSTATE.layout
    const { fullWindow: fullWindowSetting } =
        SETTINGSSTATE[SettingsType.DEFAULT]

    const fullWindow = fullWindowState && !isVertical

    const autoFullWindow =
        fullWindowSetting !== false &&
        APPSTATE.app.isMobile &&
        !isVertical &&
        inRoomOrMessage

    const newFullWindowState = fullWindow || autoFullWindow
    // console.log('[resolveFullWindow]', { autoFullWindow })
    if (fullWindowState !== newFullWindowState) {
        setFullWindow(newFullWindowState)
    }
}

const lastCalcSizeState = {
    show: null,
    fullWindow: null,
    expandedNav: false,
    expandedSearchMobile: false,
    composeFocused: false,
}

const lastCalcOrientationState = {
    portrait: true,
    width: null,
    height: null,
}

export const getEstimatedPinnedRoomWidth = ({
    width,
    vertical,
    singlePin,
    showChat,
    pinCount,
}) => {
    if (vertical) {
        if (pinCount > 1) return width / 2
        if (showChat) {
            return width - PINNED_ROOM_CHAT_MIN_WIDTH
        }
        if (singlePin) return width
        return width
    }
    if (showChat) {
        return width - PINNED_ROOM_CHAT_MIN_WIDTH
    }
    return width
}

const getContentContainerWidth = ({
    vertical,
    width,
    opened,
    ratio,
    isMobileDevice,
}) => {
    // console.log('[getContentContainerWidth]', {
    //     vertical,
    //     width,
    //     opened,
    //     ratio,
    // })
    if (opened && !vertical) {
        return Math.min(
            Math.max(ratio * width, isMobileDevice ? 360 : 480),
            width,
        )
    }
    return width
}

const getContentContainerHeight = ({
    opened,
    // passiveMode,
    vertical,
    height,
    pinContainerHeight,
}) => {
    if (opened) {
        if (vertical) {
            return height - pinContainerHeight
        }
    }
    return height
}

const getPinLimit = ({ singlePin, isMobileDevice, isVertical }) => {
    // mobile
    if (singlePin) {
        return 1
    }
    // tablet
    if (isTouchDevice && !isMobileDevice) {
        return 2
    }
    // desktop
    if (isVertical) {
        return 2
    }
    return 99
}

const checkPinnedRoomChat = (defaultShowChat: boolean) => {
    const { pinnedRooms } = ROOMSSTATE
    pinnedRooms.forEach((roomId) => {
        const room = ROOMSSTATE.rooms[roomId]
        if (!room) return
        if (room.showChat !== defaultShowChat) {
            showPinRoomChat(roomId, defaultShowChat)
        }
    })
}

const checkPinnedRoomLimit = (pinnedCount: number, pinLimit: number) => {
    if (pinnedCount <= pinLimit) return
    const { pinnedRooms } = ROOMSSTATE
    // const roomIds = Array.from(pinnedRooms)
    pinnedRooms.slice(pinnedCount - pinLimit).forEach((roomId) => {
        pinRoom(roomId, { pinned: false })
    })
}

export const getShowChat = ({
    isVertical,
    isMobileDevice,
    isTabletDevice,
    pinContainerWidth,
    pinnedRooms,
}) => {
    if (isVertical && pinnedRooms.length === 1) {
        // console.log('[getShowChat]', pinnedRooms)
        return ROOMSSTATE.rooms[pinnedRooms[0]]?.showChat
    }
    if (isVertical && isTabletDevice && pinnedRooms.length > 1) return false
    if (!isVertical && pinContainerWidth < PINNED_ROOM_MIN_WIDTH_FOR_CHAT)
        return false
    const showChat = isVertical
        ? pinnedRooms.length < 2 && !isMobileDevice
        : !isMobileDevice && pinContainerWidth > 640
    const showTabletChat =
        (isVertical && isTabletDevice && pinnedRooms.length < 2) ||
        (!isVertical && showChat)

    return isTabletDevice ? showTabletChat : showChat
}

const calculateSizes = ({ height, width, ratio, statePortrait }) => {
    const { pinnedRooms } = ROOMSSTATE
    const pinnedCount = pinnedRooms.length
    const opened = pinnedCount > 0 // && !passiveMode
    const isVertical = height > width
    const { width: usableWidth, height: usableHeight } = isVertical
        ? calculatePortraitSize(height, width)
        : calculateLandscapeSize(height, width)
    const isMobileDevice = APPSTATE.app.isMobile

    const contentContainerWidth = getContentContainerWidth({
        vertical: isVertical,
        opened,
        width: usableWidth,
        ratio,
        isMobileDevice,
    })
    const pinContainerWidth = isVertical
        ? usableWidth
        : usableWidth - contentContainerWidth

    const isTabletDevice = !isMobileDevice && isTouchDevice
    const singlePin =
        isMobileDevice || (isVertical && !(width > APP_BREAKPOINTS.lg / 2))

    const showChat = getShowChat({
        isVertical,
        isMobileDevice,
        isTabletDevice,
        pinContainerWidth,
        pinnedRooms,
    })

    // 1) check if any of the rooms should have chat removed after new orientation / resize
    if (statePortrait !== isVertical || pinContainerWidth < 540) {
        checkPinnedRoomChat(showChat)
    }
    // 2) if we're beyond the pin limit (close pins til we're down to the limit)
    const pinLimit = getPinLimit({
        singlePin,
        isMobileDevice,
        isVertical,
        // width,
    })
    checkPinnedRoomLimit(pinnedCount, pinLimit)

    const estimatedVideoWidth = getEstimatedPinnedRoomWidth({
        width: pinContainerWidth,
        vertical: isVertical,
        singlePin,
        showChat,
        pinCount: pinnedCount,
    })

    const pinContainerHeight = Math.min(
        isVertical ? (estimatedVideoWidth * 9) / 16 + 48 : height,
        PINNED_ROOM_MAX_HEIGHT,
    )
    const contentContainerHeight = getContentContainerHeight({
        height: usableHeight,
        opened,
        vertical: isVertical,
        pinContainerHeight,
    })
    // console.log(
    //     '[calculateSizes]',
    //     { showChat, estimatedVideoWidth },
    //     {
    //         // showTabletChat,
    //         usableWidth,
    //         usableHeight,
    //         pinContainerWidth,
    //         pinContainerHeight,
    //     },
    //     { contentContainerWidth, contentContainerHeight },
    // )
    return {
        contentWidth: contentContainerWidth,
        contentHeight: contentContainerHeight,
        pinsWidth: pinContainerWidth,
        pinsHeight: pinContainerHeight,
        singlePin,
        pinLimit,
    }
}

const getOrientationState = ({
    windowWidth,
    almostSquare,
    isMobile,
    portrait,
    expandedNav,
    fullWindow,
    sidebarOpen,
    expanded,
}) => {
    if (fullWindow) return FloaterPageState.FULL_WINDOW
    if (!expanded) {
        if (portrait) {
            const smallViewport =
                windowWidth > 0 ? windowWidth < APP_BREAKPOINTS.md : isMobile
            if (!smallViewport && sidebarOpen)
                return FloaterPageState.PORTRAIT_EXPANDED_SIDEBAR_CONTENT
            return FloaterPageState.PORTRAIT_CONTENT
        }
        if (sidebarOpen) {
            if (expandedNav)
                return FloaterPageState.LANDSCAPE_EXPANDED_ALL_CONTENT
            return FloaterPageState.LANDSCAPE_EXPANDED_SIDEBAR_CONTENT
        }
        if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_NAV_CONTENT
        return FloaterPageState.LANDSCAPE_CONTENT
    }
    if (portrait) {
        const smallViewport =
            windowWidth > 0 ? windowWidth < APP_BREAKPOINTS.md : isMobile
        if (!smallViewport && sidebarOpen)
            return FloaterPageState.PORTRAIT_EXPANDED_SIDEBAR
        if (almostSquare) {
            return FloaterPageState.PORTRAIT_LANDSCAPE_MEDIAN
        }
        return FloaterPageState.PORTRAIT
    }
    if (sidebarOpen) {
        if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_ALL
        return FloaterPageState.LANDSCAPE_EXPANDED_SIDEBAR
    }
    if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_NAV
    return FloaterPageState.LANDSCAPE
}

const getFullScreenRoomCoords = ({ floaterPortrait, windowWidth }) => {
    if (LAYOUTSTATE.layout.fullWindow) {
        return {
            x: 0,
            y: 0,
        }
    }
    const headerHeight = windowWidth < 960 ? 24 : 40
    if (floaterPortrait) {
        return {
            x: 0,
            y: LAYOUTSTATE.layout.expandedSearchMobile
                ? headerHeight + COMPONENT_HEIGHTS.inputSearch + 8
                : headerHeight,
        }
    } else {
        const navWidth = LAYOUTSTATE.layout.expandedNav
            ? COMPONENT_WIDTHS.leftNavExpanded
            : COMPONENT_WIDTHS.leftNav

        return {
            x: navWidth,
            y: LAYOUTSTATE.layout.expandedSearchMobile
                ? headerHeight + COMPONENT_HEIGHTS.inputSearch + 8
                : headerHeight,
        }
    }
}

const calculateLandscapeSize = (
    windowHeight: number,
    windowWidth: number,
): Size => {
    const expandedNav = LAYOUTSTATE.layout.expandedNav
    const expandedSearchMobile = LAYOUTSTATE.layout.expandedSearchMobile
    const sidebarOpen =
        SIDEBAR.state.show &&
        (APPSTATE.app.authenticated ||
            SIDEBAR.state.type === SidebarType.Participants)
    const fullWindow = LAYOUTSTATE.layout.fullWindow
    const navWidth = expandedNav
        ? COMPONENT_WIDTHS.leftNavExpanded
        : COMPONENT_WIDTHS.leftNav
    const width = Math.max(
        windowWidth -
            (fullWindow
                ? 0
                : navWidth + (sidebarOpen ? COMPONENT_WIDTHS.sidebar : 0)),
        0,
    )
    const headerHeight = windowWidth < 960 ? 24 : 40
    const height = Math.max(
        windowHeight -
            (fullWindow
                ? 0
                : expandedSearchMobile
                  ? headerHeight + COMPONENT_HEIGHTS.inputSearch + 8
                  : headerHeight),
        0,
    )
    return { height, width }
}

const calculatePortraitSize = (
    windowHeight: number,
    windowWidth: number,
): Size => {
    const expandedSearchMobile = LAYOUTSTATE.layout.expandedSearchMobile
    const sidebarOpen =
        SIDEBAR.state.show &&
        (APPSTATE.app.authenticated ||
            SIDEBAR.state.type === SidebarType.Participants)
    const width = Math.max(windowWidth - (sidebarOpen ? 180 : 0), 0)

    const headerHeight = windowWidth < 960 ? 24 : 40
    const composeFocused =
        LAYOUTSTATE.portal.composeFocused && !LAYOUTSTATE.portal.quietFocus
    const bottomInset = composeFocused ? 0 : getBrowserBottomInset()
    const height = Math.max(
        windowHeight -
            (COMPONENT_HEIGHTS.navBottom + bottomInset) -
            (expandedSearchMobile
                ? headerHeight + COMPONENT_HEIGHTS.inputSearch + 8
                : headerHeight),
        0,
    )

    return { width, height }
}

type ScreenOrientationProps = {
    ratio?: number
    // overrideSize?: Size
    overrideSkip?: boolean
}

export const setScreenOrientation = ({
    // overrideSize,
    overrideSkip,
    ratio,
}: ScreenOrientationProps = {}) => {
    const height = getHeight()
    const width = getWidth()
    const isVertical = height > width
    const {
        portrait,
        calculated,
        ratio: propsRatio,
        contentWidth: currentContentWidth,
    } = getScreenState()
    const newRatio = ratio ?? propsRatio

    // if (isVertical === portrait && calculated && !overrideSkip) return

    const {
        contentWidth,
        contentHeight,
        pinsWidth,
        pinsHeight,
        // showChat,
        singlePin,
        pinLimit,
    } = calculateSizes({
        height,
        width,
        ratio: newRatio,
        statePortrait: portrait,
    })

    // console.log('[setScreenOrientation] pre skip', {
    //     isVertical,
    //     stateVertical: portrait,
    //     height,
    //     width,
    //     skipping:
    //         contentWidth === currentContentWidth &&
    //         isVertical === portrait &&
    //         calculated &&
    //         !overrideSkip,
    //     sidebar:
    //         SIDEBAR.state.show &&
    //         (APPSTATE.app.authenticated ||
    //             SIDEBAR.state.type === SidebarType.Participants),
    //     authenticated: APPSTATE.app.authenticated,
    //     sidebarState: SIDEBAR.state,
    // })
    if (
        contentWidth === currentContentWidth &&
        isVertical === portrait &&
        calculated &&
        !overrideSkip
    )
        return

    const { width: roomWidth, height: roomHeight } = isVertical
        ? calculatePortraitSize(height, width)
        : calculateLandscapeSize(height, width)

    // console.log(
    //     '[setScreenOrientation] after',
    //     { isVertical, height, width },
    //     { roomWidth, roomHeight },
    // )
    const almostSquare = contentWidth <= height && contentWidth > contentHeight

    const orientationType = getOrientationState({
        almostSquare,
        windowWidth: width,
        portrait: isVertical,
        expandedNav: LAYOUTSTATE.layout.expandedNav,
        fullWindow: LAYOUTSTATE.layout.fullWindow,
        sidebarOpen:
            SIDEBAR.state.show &&
            (APPSTATE.app.authenticated ||
                SIDEBAR.state.type === SidebarType.Participants),
        isMobile: APPSTATE.app.isMobile,
        expanded: getModalState().floating_room.expanded,
    })

    const { x: roomX, y: roomY } = getFullScreenRoomCoords({
        floaterPortrait: isVertical,
        windowWidth: width,
    })

    const pinnedCount = ROOMSSTATE.pinnedRooms.length
    const hasPinnedRooms = pinnedCount > 0
    const pinLimitReached = pinnedCount >= pinLimit

    setScreenState({
        portrait: isVertical,
        roomHeight,
        roomWidth,
        roomX,
        roomY,
        calculated: true,
        windowWidth: width,
        windowHeight: height,
        contentWidth,
        contentHeight,
        pinsWidth,
        pinsHeight,
        singlePin,
        pinLimit,
        ratio: newRatio,
        orientationType,
        hasPinnedRooms,
        pinLimitReached,
    })
}

const checkAndRepositionOffscreenFloaters = () => {
    const { floating_room: floatingRoom, floater: floaters } = getModalState()
    repositionFloater(floatingRoom)
    // 1) loop through the opened floaters
    Object.keys(floaters).forEach((modalKey: string) => {
        const floater = floaters[modalKey]

        if (floater.type === FLOATING_PREVIEW && !floater.show) {
            closeFloatingPreview(floater.id)
        }
        // don't reposition chat heads and whatever else can be minimized,
        // as `floaterMomentum` is responsible for that
        if (!floater.minimized) repositionFloater(floater)
    })
}

const debouncedFloaterCheck = debounce(checkAndRepositionOffscreenFloaters, 400)

const calcFullscreenSizeEvents = () => {
    const size = {
        width: getWidth(),
        height: getHeight(),
    }
    const newPortrait = size.width < size.height
    if (
        newPortrait !== lastCalcOrientationState.portrait ||
        size.width !== lastCalcOrientationState.width ||
        size.height !== lastCalcOrientationState.height
    ) {
        lastCalcOrientationState.portrait = newPortrait
        setScreenOrientation({ overrideSkip: true })
        debouncedFloaterCheck()
    }
}

// this is kind of a messy catch all for the pubSubs
interface UpdateFullscreenOptions {
    show?: boolean // whether sidebar is showing
    fullWindow?: boolean
    overrideSize?: Size
    expandedNav?: boolean
    expandedSearchMobile?: boolean
    composeFocused?: boolean
}

export const calcFullscreenSizeSub = (options?: UpdateFullscreenOptions) => {
    const {
        show, // sidebar
        fullWindow, // layout
        expandedNav, // layout
        expandedSearchMobile, // layout
        composeFocused, // portal
        // overrideSize = { width: getWidth(), height: getHeight() }, // handleResize
    } = options || {}
    // eslint-disable-next-line fp/no-let
    let changed = false
    // console.log('[calcFullscreenSizeSub]', options, lastCalcSizeState)
    if (show !== undefined && lastCalcSizeState.show !== show) {
        changed = true
        lastCalcSizeState.show = show
    }

    // any of the shared values (layout ones particularly) will end up triggering an early bail because they are present but haven't changed
    // -- check them all and track if any have changed and return then instead of potentially after each check

    if (fullWindow !== undefined) {
        if (lastCalcSizeState.fullWindow !== fullWindow) changed = true
        lastCalcSizeState.fullWindow = fullWindow
    }
    if (
        expandedNav !== undefined &&
        lastCalcSizeState.expandedNav !== expandedNav
    ) {
        changed = true
        lastCalcSizeState.expandedNav = expandedNav
    }
    if (
        expandedSearchMobile !== undefined &&
        lastCalcSizeState.expandedSearchMobile !== expandedSearchMobile
    ) {
        changed = true
        lastCalcSizeState.expandedSearchMobile = expandedSearchMobile
    }
    if (
        composeFocused !== undefined &&
        lastCalcSizeState.composeFocused !== composeFocused
    ) {
        changed = true
        lastCalcSizeState.composeFocused = composeFocused
    }

    if (changed) {
        // updateFullscreenSize()
        setScreenOrientation({ overrideSkip: true })
    }
}

/// Floating event subs
pubSubLayout.on(LayoutEvent.PORTAL_CHANGE, calcFullscreenSizeSub)

if (typeof window !== 'undefined') {
    const eventTarget = isScreenOrientationSupported
        ? window.screen.orientation
        : window
    const eventName = isScreenOrientationSupported
        ? 'change'
        : 'orientationchange'

    const timerIDs: Array<Timeout> = []

    const handleOrientationChange = () => {
        setScreenOrientation()
        resolveFullWindow()
    }

    const handleResize = () => {
        timerIDs.forEach(clearTimeout)
        calcFullscreenSizeEvents()
        resolveFullWindow()
        if (isIDevice) {
            timerIDs[0] = setTimeout(
                () => setScreenOrientation({ overrideSkip: true }),
                500,
            )
            timerIDs[1] = setTimeout(
                () => setScreenOrientation({ overrideSkip: true }),
                1000,
            )
            timerIDs[2] = setTimeout(
                () => setScreenOrientation({ overrideSkip: true }),
                2000,
            )
        }
    }

    window.addEventListener('resize', handleResize, {
        passive: true,
    })

    // Add event listener
    eventTarget.addEventListener(eventName, handleOrientationChange, {
        passive: true,
    })
}
